
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "@vds/icons";
import { Body } from "@vds/typography";
import { ImageDownloadMedia } from "../../model/mediaService";
import { saveStreamImageMapping, saveHsEntityImageMapping } from "../../store/actions";
import { getPairedCamSnapShotIds } from "../../model/cameraonboardingService";
import "./search.css";
import { FOV_IMAGE, ENTITY_IMAGE, FORENSIC_IMAGE_DOWNLOAD_IDENTIFIER, SNAPSHOT_IMAGE_DOWNLOAD_IDENTIFIER, NO_IMAGE_PLACEHOLDER, LAZY_IMAGE_MESSAGES } from "./SearchConstants";
import { isS3UrlValid } from "./ForensicSearchUtil";
import BoundingBoxOverlay from "./BoundingBoxOverlay";

// Module-level (shared across every LazyImage mount) - tracks entity imageIds with an
// ImageDownloadMedia call currently in flight. Page navigation unmounts/remounts LazyImage
// instances well before a slow fetch settles; without this, each remount for the same imageId
// fired its own duplicate download (count kept growing: 6 pending -> revisit -> 12 -> ...).
const inFlightEntityFetches = new Set();

const LazyImage = ({ steamId, imageFor, entity, isBeingRetried, onRefresh }) => {
    const imageReducer = useSelector((state) => state.imagesReducer);
    const dispatch = useDispatch();
    const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
    const [streamImageUrl, setImageUrl] = useState(NO_IMAGE_PLACEHOLDER);
    const deviceListItem = useSelector((state) => state.deviceReducer);
    const fovList = deviceListItem.fovList || [];
    const [noImageText, setNoImageText] = useState(LAZY_IMAGE_MESSAGES.FETCHING_SNAPSHOT);
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
    // Guards fetchImage/handlePairedSnapshot's post-await setState/dispatch calls - user can
    // navigate to another page (unmounting this entry) before the in-flight download settles;
    // without this, that stale response would still setState/dispatch against an unmounted
    // component and could clobber whichever entry now occupies this slot.
    const isMountedRef = useRef(true);
    useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);


    useEffect(() => {
      if (imageFor == ENTITY_IMAGE) {
        const entityImageDetail = imageReducer.hsEntityImages.find((img) => img.id === steamId);
        if (entityImageDetail !== undefined) {
          setImageUrl(entityImageDetail?.imageUrl);
        } else if (entity?.status === "failed" && !isBeingRetried) {
          setImageUrl(NO_IMAGE_PLACEHOLDER);
          setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
        } else if (entity?.status === "failed" && isBeingRetried) {
          // Entry is being retried — show loading state. If the retry also fails,
          // isBeingRetried flips back to false and this effect re-fires, falling into
          // the branch above to restore the error template.
          setImageUrl(NO_IMAGE_PLACEHOLDER);
          setNoImageText(LAZY_IMAGE_MESSAGES.FETCHING_SNAPSHOT);
        } else {
          if (entity?.s3url && isS3UrlValid(entity.s3url)) {
            // Presigned S3 URL is still valid — use it directly, no backend roundtrip needed.
            setImageUrl(entity.s3url);
            if (steamId) {
              dispatch(saveHsEntityImageMapping(steamId, entity.s3url, entity?.timestamp));
            }
          } else {
            // No s3url or URL has expired — fall back to the download API.
            setNoImageText(LAZY_IMAGE_MESSAGES.FETCHING_SNAPSHOT);
            if (steamId && !inFlightEntityFetches.has(steamId)) {
                inFlightEntityFetches.add(steamId);
                fetchImage(null, steamId, entity?.timestamp, FORENSIC_IMAGE_DOWNLOAD_IDENTIFIER);
            }
          }
        }
      } else if (imageFor == FOV_IMAGE) {
        if (steamId) {
          const fovImageDetail = imageReducer.streamImages.find((img) => img.id === steamId);
          if (fovImageDetail !== undefined) {
            setImageUrl(fovImageDetail?.imageUrl);
          } else {
            const fov = fovList.find((f) => f.id === steamId);
            if (fov !== undefined) {
              const imageid = fov?.customdata?.imageid;
              if (imageid) {
                fetchImage(steamId, imageid, fov?.customdata?.imagetimestamp, SNAPSHOT_IMAGE_DOWNLOAD_IDENTIFIER);
              } else {
                handlePairedSnapshot(fov);
              }
            }
            setImageUrl(NO_IMAGE_PLACEHOLDER);
            setNoImageText(LAZY_IMAGE_MESSAGES.SNAPSHOT_NOT_AVAILABLE);
          }
        }
      }
      // imageReducer.hsEntityImages is included so an instance that skipped its own fetch
      // (deduped against an in-flight one from a since-unmounted instance, see
      // inFlightEntityFetches above) re-checks the cache once that fetch resolves elsewhere,
      // instead of being stuck on the "Fetching snapshot" placeholder forever.
      // isBeingRetried is included so the effect re-fires when a retry starts (show loading)
      // or when it completes (isBeingRetried flips false while entity?.status is still "failed",
      // which would not trigger a re-fire on entity?.status alone).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steamId, entity?.status, imageReducer.hsEntityImages, isBeingRetried]);

    // Tracks the rendered <img> box (not the wrapper div, which can be taller due to its
    // minHeight placeholder) so the bounding-box overlay lines up even when objectFit:contain
    // letterboxes the image and the wrapper is responsive (size can change on resize).
    useEffect(() => {
      if (!imgRef.current || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      });
      observer.observe(imgRef.current);
      return () => observer.disconnect();
    }, [streamImageUrl]);

    const handleImageLoad = (e) => {
      setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
    };

    // Fired when the browser fails to load the image src (S3 expired/rejected, network error, etc.).
    // Falls back to the download API. If the URL that failed was already a blob: (i.e. came from
    // ImageDownloadMedia), the download API won't help — just show the error state instead.
    const handleImageError = () => {
        if (!isMountedRef.current) return;
        if (streamImageUrl.startsWith('blob:')) {
            setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
            setImageUrl(NO_IMAGE_PLACEHOLDER);
            return;
        }
        console.warn("LazyImage: entity image failed to load, falling back to download API");
        setImageUrl(NO_IMAGE_PLACEHOLDER);
        if (steamId) {
            fetchImage(null, steamId, entity?.timestamp, FORENSIC_IMAGE_DOWNLOAD_IDENTIFIER);
        }
    };

    const fetchImage = async (streamId, imageid, timeStamp, mediaSource = "") => {
        try {
            let url = await ImageDownloadMedia(imageid, activeSession, streamId, mediaSource);
            if (imageFor == ENTITY_IMAGE) inFlightEntityFetches.delete(imageid);
            if (url) {
                // Dispatch unconditionally (even if this instance has since unmounted) - it's a
                // global, imageId-keyed cache update with no clobber risk, unlike the setState
                // calls below. Skipping it here would mean an abandoned-but-successful fetch
                // never reaches the shared cache, so a later remount for the same imageId would
                // cache-miss and re-fetch again forever.
                if(imageFor == FOV_IMAGE){
                    dispatch(saveStreamImageMapping(streamId, url, timeStamp));
                }
                else if(imageFor == ENTITY_IMAGE){
                    dispatch(saveHsEntityImageMapping(imageid, url, timeStamp));
                }
                if (!isMountedRef.current) return;
                setImageUrl(url);
            } else {
                if (!isMountedRef.current) return;
                setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
                console.log("Image download Error", url);
            }
        } catch (error) {
            if (imageFor == ENTITY_IMAGE) inFlightEntityFetches.delete(imageid);
            if (!isMountedRef.current) return;
            setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
            console.error("Error downloading image:", error);
        }
    };

    const handlePairedSnapshot = async (fov) => {
        let streamId = fov.id;
        try {
            const cameraDetails = deviceListItem.cameraLists.find((f) => f.id === fov.parentdeviceid);
            const fovData = await getPairedCamSnapShotIds(activeSession, cameraDetails?.[0]?.qrcode, streamId);
            if (!isMountedRef.current) return;
            if (fovData?.[0]?.imageid) {
                fetchImage(fovData[0].imageid, fovData[0].imagetimestamp, SNAPSHOT_IMAGE_DOWNLOAD_IDENTIFIER);
            } else {
                setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            setNoImageText(LAZY_IMAGE_MESSAGES.FETCH_FAILED);
        }
    };

    const isFailed = noImageText === LAZY_IMAGE_MESSAGES.FETCH_FAILED;

    return (
        <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
            {streamImageUrl === NO_IMAGE_PLACEHOLDER && (
                <div
                    style={{
                        paddingLeft: "5px",
                        width: "100%",
                        // Match the loaded <img>'s aspect-ratio-based sizing (below) instead of a
                        // fixed height - the real image renders as a square matching the
                        // container's width, so a fixed 200px placeholder is only right by
                        // coincidence and otherwise causes a visible size jump when it swaps in.
                        aspectRatio: "1/1",
                        backgroundColor: isFailed ? "#FFECE0" : "#f0f0f0",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {isFailed && onRefresh ? (
                      <div
                        className="hs-thumb-refresh-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRefresh();
                        }}
                        title="Retry"
                      >
                        <Icon name="sync" size="small" color="#000000" />
                      </div>
                    ) : (
                      isFailed && <Icon name="error" size="small" color="#6f7171" />
                    )}
                    <Body><span className={!isFailed ? "hs-loading-text" : undefined}>{noImageText}</span></Body>
                </div>
            )}
            {streamImageUrl !== NO_IMAGE_PLACEHOLDER && streamImageUrl !== "" && (
                <img
                    ref={imgRef}
                    src={streamImageUrl}
                    onLoad={handleImageLoad}
                    onError={imageFor === ENTITY_IMAGE ? handleImageError : undefined}
                    style={{
                        width: "100%",
                        height: "auto",
                        backgroundColor: "black",
                        aspectRatio: "1/1",
                        objectFit: "contain",
                    }}
                    alt="Snapshot"
                />
            )}
            {streamImageUrl !== NO_IMAGE_PLACEHOLDER && streamImageUrl !== "" && entity?.boundingBox && (
                <BoundingBoxOverlay
                    boundingBox={entity.boundingBox}
                    containerSize={containerSize}
                    naturalSize={naturalSize}
                />
            )}
        </div>
    );
};

export default LazyImage;